import { useCallback } from 'react';

import { useResponse } from 'contexts/response';
import { useNavigate } from 'react-router';
import { ActiveUnit, Booking, Customer, UnitGrade, UnitStatus } from 'types';
import GateUnit from 'types/gate-unit';
import { isGateUnit, isUnit } from 'utils';

export interface UnitTransfer {
  id: number;
  oldUnit: ActiveUnit;
  newUnit: ActiveUnit;
}

export interface UnitTransferRequestParams {
  id?: number;
  booking?: Booking;
  customer?: Customer;
  chargeGateIn?: boolean;
  chargeGateOut?: boolean;
  ediGateIn?: boolean;
  ediGateOut?: boolean;
  redeliveryId?: number;
  transferDate?: string;
  unitNumber?: string;
}

/* endpoints */
/* /units/{id}/customer
    ?unitNumber=String
    &transferDate=OffsetDateTime
    &chargeGateIn=Boolean
    &chargeGateOut=Boolean
    &ediGateIn=Boolean
    &ediGateOut=Boolean */
/* /units/{id}/on-hold */
/* /units/{id}/hold-notes?onHold=Boolean */
/* /units/{id}/unit-grade */
/* /units/{id}/notes?unitGrade=UnitGrade */
/* /units/{id}/unit-status */
/* /units/{id}/status-reason?unitStatus=UnitStatus&resendEDI=Boolean */

export const useUnitApi = () => {
  const navigate = useNavigate();
  const { handleError, setSuccess } = useResponse();

  const findReservedUnit = useCallback(async (unitNumber: string): Promise<GateUnit | null> => {
    try {
      const response = await fetch(new URL(`/api/units/reserved/unitNumber/${unitNumber}`, window.location.origin), {
        method: 'GET',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const json = await response.json();
        if (isGateUnit(json)) return json;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }, []);

  const canTransferUnit = useCallback(async (unitId: number) => {
    const response = await fetch(new URL(`/api/transfers?oldUnitId=${unitId}`, window.location.origin), {
      method: 'GET',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    try {
      const transfer = await response.json();
      return !transfer;
    } catch (_) {
      return true;
    }
  }, []);

  const getUnitTransfers = useCallback(async (unitId: number): Promise<UnitTransfer[]> => {
    const response = await fetch(new URL(`/api/transfers/${unitId}`, window.location.origin), {
      method: 'GET',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
    const json = await response.json();
    return Array.isArray(json) ? json : [];
  }, []);

  const transferUnit = useCallback(
    async ({ customer, id, ...args }: UnitTransferRequestParams) => {
      if (!customer || !id) {
        return null;
      }

      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(customer)
      };

      const url = new URL(`/api/units/${id}/customer`, window.location.origin);

      if (args.chargeGateIn !== undefined) {
        url.searchParams.append('chargeGateIn', `${args.chargeGateIn}`);
      }
      if (args.chargeGateOut !== undefined) {
        url.searchParams.append('chargeGateOut', `${args.chargeGateOut}`);
      }
      if (args.ediGateIn !== undefined) {
        url.searchParams.append('ediGateIn', `${args.ediGateIn}`);
      }
      if (args.ediGateOut !== undefined) {
        url.searchParams.append('ediGateOut', `${args.ediGateOut}`);
      }
      if (args.booking?.id) {
        url.searchParams.append('bookingId', `${args.booking.id}`);
      }
      if (args.transferDate) {
        url.searchParams.append('transferDate', args.transferDate);
      }
      if (args.redeliveryId) {
        url.searchParams.append('redeliveryId', args.redeliveryId + '');
      }
      if (args.unitNumber) {
        url.searchParams.append('unitNumber', args.unitNumber);
      }
      try {
        if (!(await canTransferUnit(id))) {
          handleError('Unit has already been transferred');
          return null;
        }

        const response = await fetch(url, init);
        const json = await response.json();

        if (response.ok) {
          setSuccess(`Unit updated: ${json?.unitNumber} (${json?.customer?.name})`);
          return json;
        }

        handleError(json?.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
        return null;
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [canTransferUnit, handleError, setSuccess]
  );

  const updateOnHold = useCallback(
    async (id: number, holdNotes: string | undefined | null, onHold: boolean): Promise<ActiveUnit | void> => {
      const hasNotes = !!holdNotes?.trim();
      const input: URL = new URL(`/api/units/${id}/${hasNotes ? 'hold-notes' : 'on-hold'}`, window.location.origin);
      if (hasNotes) input.searchParams.append('onHold', `${onHold}`);

      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: hasNotes ? holdNotes : JSON.stringify(onHold)
      };

      try {
        const response = await fetch(input, init);
        if (!response.ok) return await handleError(response);

        const json = await response.json();
        if (isUnit(json)) {
          if (json.id !== id || json.onHold !== onHold) {
            return await handleError('The server did not confirm the requested hold status. Refresh and try again.');
          }
          setSuccess(
            <dl className="grid-2">
              <dt className="text-info-main">UNIT NUMBER</dt>
              <dd className="text-info-dark">{json.unitNumber}</dd>
              <dt className="text-info-main">ON HOLD</dt>
              <dd className="text-info-dark">{json.onHold ? 'YES' : 'NO'}</dd>
              <dt className="text-info-main">HOLD NOTES</dt>
              <dd className="text-info-dark">{json.holdNotes}</dd>
            </dl>
          );
          return json;
        }
        return await handleError(response, json);
      } catch (e) {
        return await handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const updateUnitGrade = useCallback(
    async (id: number, notes: string | undefined | null, unitGrade: UnitGrade): Promise<ActiveUnit | void> => {
      const hasNotes = !!notes?.trim();
      const input: URL = new URL(`/api/units/${id}/${hasNotes ? 'notes' : 'unit-grade'}`, window.location.origin);
      if (hasNotes) input.searchParams.append('unitGrade', unitGrade);

      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: hasNotes ? notes : unitGrade
      };

      try {
        const response = await fetch(input, init);
        if (!response.ok) return await handleError(response);

        const json = await response.json();
        if (isUnit(json)) {
          if (json.id !== id || json.unitGrade !== unitGrade) {
            return await handleError('The server did not confirm the requested unit grade. Refresh and try again.');
          }
          setSuccess(
            <dl className="grid-2">
              <dt className="text-info-main">UNIT NUMBER</dt>
              <dd className="text-info-dark">{json.unitNumber}</dd>
              <dt className="text-info-main">UNIT GRADE</dt>
              <dd className="text-info-dark">{json.unitGrade}</dd>
              <dt className="text-info-main">NOTES</dt>
              <dd className="text-info-dark">{json.notes}</dd>
            </dl>
          );
          return json;
        }
        return await handleError(response, json);
      } catch (e) {
        return await handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const updateUnitSizeType = useCallback(
    async (id: number, sizeType: string): Promise<ActiveUnit | void> => {
      const input: URL = new URL(`/api/units/${id}/size-type`, window.location.origin);

      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify({ sizeType })
      };

      try {
        const response = await fetch(input, init);
        if (!response.ok) return await handleError(response);

        const json = await response.json();
        if (isUnit(json)) {
          if (json.id !== id || json.sizeType !== sizeType) {
            return await handleError('The server did not confirm the requested size type. Refresh and try again.');
          }
          setSuccess(
            <dl className="grid-2">
              <dt className="text-info-main">UNIT NUMBER</dt>
              <dd className="text-info-dark">{json.unitNumber}</dd>
              <dt className="text-info-main">SIZE TYPE</dt>
              <dd className="text-info-dark">{json.sizeType}</dd>
            </dl>
          );
          return json;
        }
        return await handleError(response, json);
      } catch (e) {
        return await handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const updateUnitStatus = useCallback(
    async (
      id: number,
      resendEDI: boolean,
      statusReason: string | undefined | null,
      unitStatus: UnitStatus
    ): Promise<ActiveUnit | void> => {
      const input: URL = new URL(`/api/units/${id}/status-reason`, window.location.origin);
      input.searchParams.append('resendEDI', `${resendEDI}`);
      input.searchParams.append('unitStatus', `${unitStatus}`);

      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: statusReason?.trim() ? statusReason : null
      };

      try {
        const response = await fetch(input, init);
        if (!response.ok) return await handleError(response);

        const json = await response.json();
        if (isUnit(json)) {
          if (json.id !== id || json.unitStatus !== unitStatus) {
            return await handleError('The server did not confirm the requested unit status. Refresh and try again.');
          }
          setSuccess(
            <dl className="grid-2">
              <dt className="text-info-main">UNIT NUMBER</dt>
              <dd className="text-info-dark">{json.unitNumber}</dd>
              <dt className="text-info-main">UNIT STATUS</dt>
              <dd className="text-info-dark">{json.unitStatus}</dd>
              <dt className="text-info-main">STATUS NOTES</dt>
              <dd className="text-info-dark">{json.statusReason}</dd>
            </dl>
          );
          return json;
        }
        return await handleError(response, json);
      } catch (e) {
        return await handleError(e);
      }
    },
    [handleError, setSuccess]
  );

  const updateUnit = useCallback(
    async (id: number, unit: ActiveUnit): Promise<ActiveUnit | void> => {
      try {
        const response = await fetch(new URL(`/api/units/${id}`, window.location.origin), {
          method: 'PUT',
          headers: new Headers({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }),
          body: JSON.stringify(unit)
        });
        if (response.ok) {
          const json = await response.json();
          if (isUnit(json)) {
            setSuccess(`Unit ${json.unitNumber} updated`);
            navigate(`/units/${json.id}?location=${json.location?.id}`, { replace: true });
          } else await handleError(response, json);
        } else await handleError(response);
      } catch (e) {
        await handleError(e);
      }
    },
    [handleError, navigate, setSuccess]
  );

  return {
    canTransferUnit,
    getUnitTransfers,
    findReservedUnit,
    transferUnit,
    updateOnHold,
    updateUnit,
    updateUnitGrade,
    updateUnitSizeType,
    updateUnitStatus
  };
};
